/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale Google.We = NO -->*/
/*Allow for use of "We" in sensitive topics like pricing and security -->*/
/*<!-- vale Google.We = YES -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Self-serve pricing", description = "Learn about our self-serve pricing and how Hightouch counts and categorizes destinations.", lastUpdatedDate = new Date(1686009600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Self-serve pricing",
        description: "Learn about our self-serve pricing and how Hightouch counts and categorizes destinations.",
        lastUpdatedDate: "Tue Jun 06 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Destinations",
          "depth": 0,
          "id": "destinations"
        }, {
          "value": "What counts as a destination?",
          "depth": 0,
          "id": "what-counts-as-a-destination"
        }, {
          "value": "Standard destinations",
          "depth": 1,
          "id": "standard-destinations"
        }, {
          "value": "Lite destinations",
          "depth": 1,
          "id": "lite-destinations"
        }, {
          "value": "Grouped destinations",
          "depth": 1,
          "id": "grouped-destinations"
        }, {
          "value": "Premium destinations",
          "depth": 1,
          "id": "premium-destinations"
        }, {
          "value": "Custom destinations",
          "depth": 1,
          "id": "custom-destinations"
        }, {
          "value": "Destination trials",
          "depth": 0,
          "id": "destination-trials"
        }, {
          "value": "Other considerations",
          "depth": 0,
          "id": "other-considerations"
        }, {
          "value": "Active syncs",
          "depth": 1,
          "id": "active-syncs"
        }, {
          "value": "User seats",
          "depth": 1,
          "id": "user-seats"
        }, {
          "value": "Workspaces",
          "depth": 1,
          "id": "workspaces"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      strong: "strong",
      em: "em",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      h3: "h3",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: ["\n", "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["This page describes Self-serve pricing (Free, Starter, Pro plans)\nconsiderations. Please visit the ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "pricing page"
          }), "\nto compare the complete list of features each plan includes. Refer to the\n", _jsx(_components.a, {
            href: "/pricing/bt-pricing",
            children: "Business tier pricing"
          }), " page to learn more about Business\ntier pricing considerations."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch Self-serve pricing is primarily based on the ", _jsx(_components.strong, {
          children: "number of destinations"
        }), " in your workspace.\nWe don't limit the number of sources, source rows, or destination data fields you can sync into each destination*.\nYou also have an unlimited number of sync runs per ", _jsx(_components.a, {
          href: "#other-considerations",
          children: "active sync"
        }), ".\nThis structure lets you sync as many fields as you want, as frequently as you want, without worrying about overages."]
      }), "\n", _jsxs(_components.p, {
        children: ["* ", _jsxs(_components.em, {
          children: ["To ensure we can serve all customers sustainably, we impose an operations limit of 100,000,000 operations per month.\nThe majority of workspaces fall under this limit.\nDon't hesitate to ", _jsx(PylonLink, {
            pylonText: " ",
            linkText: "get in touch"
          }), " if you're concerned about the limit."]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "destinations",
        children: "Destinations"
      }), "\n", _jsxs(_components.p, {
        children: ["The total number of billable destinations in your workspace is the distinct count of each ", _jsx(_components.strong, {
          children: "destination type"
        }), " at the end of each billing period."]
      }), "\n", _jsx(_components.p, {
        children: "A destination type is a destination that you see in our destination catalog.\nFor example, Salesforce, HubSpot, Google Ads, and Iterable are all destination types."
      }), "\n", _jsx(_components.p, {
        children: "Multiple instances of the same destination type are deduplicated.\nFor example, having three separate Salesforce accounts in your workspace counts as only one billable destination."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Destination Instances"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Total Billable Destinations"
              })
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Salesforce, Iterable, HubSpot"
            }), _jsx(_components.td, {
              children: "3"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Salesforce, Salesforce, Iterable, HubSpot"
            }), _jsx(_components.td, {
              children: "3"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Salesforce, Facebook Custom Audiences, Facebook Conversions API"
            }), _jsx(_components.td, {
              children: "2"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "what-counts-as-a-destination",
        children: "What counts as a destination?"
      }), "\n", _jsx(_components.p, {
        children: "A destination counts towards your total number of billable destinations as long as it's added to your workspace.\nThis applies whether there are ten active syncs or zero active syncs tied to the destination.\nCounting all destinations in a workspace, regardless of activity, enables transparent and objective destination counting."
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "We recommend deleting unused destinations so they don't add to your billable\ndestinations."
        })
      }), "\n", _jsx(_components.h3, {
        id: "standard-destinations",
        children: "Standard destinations"
      }), "\n", _jsxs(_components.p, {
        children: ["Most destinations in Hightouch are standard billable destinations.\nYou can find these in our ", _jsx(_components.a, {
          href: "https://hightouch.com/integrations",
          children: "destination catalog"
        }), ".\nEach type of standard billable destination counts as one destination towards your allotment."]
      }), "\n", _jsx(_components.h3, {
        id: "lite-destinations",
        children: "Lite destinations"
      }), "\n", _jsxs(_components.p, {
        children: ["Lite destinations don't count towards your total number of billable destinations.\nYou can add up to 25 ", _jsx(_components.a, {
          href: "#other-considerations",
          children: "active syncs"
        }), " in total for any of our lite destinations to your workspace."]
      }), "\n", _jsx(_components.p, {
        children: "These are lite destinations in Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Airtable"
        }), "\n", _jsx(_components.li, {
          children: "Google Sheets"
        }), "\n", _jsx(_components.li, {
          children: "Google Sheets Service Account"
        }), "\n", _jsx(_components.li, {
          children: "Microsoft Excel"
        }), "\n", _jsx(_components.li, {
          children: "Microsoft Teams"
        }), "\n", _jsx(_components.li, {
          children: "Mattermost"
        }), "\n", _jsx(_components.li, {
          children: "Slack"
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/sync-logs",
            children: "Warehouse Sync Logs placeholder destinations"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "grouped-destinations",
        children: "Grouped destinations"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "https://hightouch.com/integrations",
          children: "integration catalog"
        }), " lists some destination types separately for clarity even though they count as one destination.\nThese destinations are:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Facebook"
          }), ": Facebook Conversions API, Facebook Offline Conversions, Facebook Custom Audiences, Facebook Product Catalog"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Salesforce"
          }), ": Salesforce and Salesforce (Sandbox)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Braze"
          }), ": Braze and Braze Cohorts"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Gainsight"
          }), ": Gainsight and Gainsight PX"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "premium-destinations",
        children: "Premium destinations"
      }), "\n", _jsxs(_components.p, {
        children: ["Premium destinations count towards your total number of billable destinations and are only available on our ", _jsx(_components.a, {
          href: "/pricing/bt-pricing",
          children: "Business tier pricing"
        }), ".\nUsing any premium destination requires you to be on a Business tier plan.\nThese are premium destinations in Hightouch:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Finance"
          }), ": NetSuite, Anaplan, Workday"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Enterprise marketing automation"
          }), ": Acoustic, Adobe Target, Bloomreach, Emarsys, Marketo, Oracle Eloqua, Oracle Responsys, Salesforce Marketing Cloud"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Enterprise resource planning and customer relationship management"
          }), ": Microsoft Dynamics 365"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Advanced Google advertising tools"
          }), ": Google Advertising Manager, Google Campaign Manager, DV360"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "custom-destinations",
        children: "Custom destinations"
      }), "\n", _jsx(_components.p, {
        children: "Custom destinations let you send data to internal tools and third-party APIs not supported in our catalog of native destinations.\nAs with other destinations, each type of custom destination only counts towards your total number of billable destinations once."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Scenario"
              })
            }), _jsx(_components.th, {
              children: _jsx(_components.strong, {
                children: "Total Billable Destinations"
              })
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "5 HTTP requests to 5 different APIs"
            }), _jsx(_components.td, {
              children: "1 (HTTP request)"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "10 AWS Lambda syncs and 3 HTTP requests"
            }), _jsx(_components.td, {
              children: "2 (AWS Lambda and HTTP request)"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "5 Kafka syncs, 7 Azure Functions, and 1 Postgres sync"
            }), _jsx(_components.td, {
              children: "3 (Kafka sync, Azure Function, and Postgres sync)"
            })]
          })]
        })]
      }), "\n", _jsx(Info, {
        children: "Webhooks and HTTP requests count as the same destination type."
      }), "\n", _jsx(_components.p, {
        children: "These are custom destinations in Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "HTTP Request"
        }), "\n", _jsx(_components.li, {
          children: "Webhook"
        }), "\n", _jsx(_components.li, {
          children: "AWS Lambda"
        }), "\n", _jsx(_components.li, {
          children: "Google Cloud Functions"
        }), "\n", _jsx(_components.li, {
          children: "Azure Functions"
        }), "\n", _jsx(_components.li, {
          children: "Embedded Destination"
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "It's strictly prohibited to use a custom destination to replicate\nfunctionality already supported by a native destination."
        })
      }), "\n", _jsx(_components.h2, {
        id: "destination-trials",
        children: "Destination trials"
      }), "\n", _jsx(_components.p, {
        children: "Each destination comes with a free 30-day trial.\nYou can install, test, and experiment with as many destinations as you like."
      }), "\n", _jsx(_components.p, {
        children: "The trial begins when you add the destination's first instance to your workspace.\nDuring the 30-day trial, any instance of the destination doesn't count towards your total number of billable destinations."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch doesn't enforce a limit on the number of destinations that you can try at any given time."
      }), "\n", _jsxs(_components.p, {
        children: ["Ready to give it a try? ", _jsx(_components.a, {
          href: "https://app.hightouch.com/signup",
          children: "Get started"
        }), " today."]
      }), "\n", _jsx(_components.h2, {
        id: "other-considerations",
        children: "Other considerations"
      }), "\n", _jsxs(_components.p, {
        children: ["Besides destinations, self-serve plans have limits based on a few other factors.\nRefer to the ", _jsx(_components.a, {
          href: "https://hightouch.com/pricing",
          children: "pricing page"
        }), " to see the exact limitations per plan."]
      }), "\n", _jsx(_components.h3, {
        id: "active-syncs",
        children: "Active syncs"
      }), "\n", _jsx(_components.p, {
        children: "Self-serve plans allow a limited number of active syncs per billable destination.\nTo count as active, a sync must have run at least once in the past 28 days.\nDeleted syncs are considered active for 28 days after the last sync run."
      }), "\n", _jsx(_components.h3, {
        id: "user-seats",
        children: "User seats"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/workspace-management/overview#core-concepts",
          children: "Workspaces"
        }), " on self-serve plans have a limit on the number of team members they can have."]
      }), "\n", _jsx(_components.h3, {
        id: "workspaces",
        children: "Workspaces"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/workspace-management/overview#core-concepts",
          children: "Organizations"
        }), " on self-serve plans have a limit on the number of workspaces they can include."]
      }), "\n"]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
